/**
* Localized NAATI comparison cluster — /{locale}/naati/compare/{slug}.
*
* Hand-written per-locale content (NOT a machine translation of the Thai
* cluster). Each entry mirrors the Thai slug set so hreflang stays reciprocal.
*
* Accuracy policy
* - NAATI certification = accuracy of the translation, issued by a
* practitioner listed in the NAATI Practitioner Directory (naati.com.au).
* - Thai Department of Consular Affairs (MFA) legalisation = authenticity of
* the document / official signature, not translation quality.
* - Thailand acceded to the HCCH Apostille Convention on 30 June 2026; entry
* into force 28 February 2027 (see src/data/legal-facts.ts). Until then the
* embassy legalisation chain still applies.
* - Fees and turnaround are ranges marked "approximate". Acceptance is always
* at the receiving authority's discretion.
*/
import type { NaatiLocale } from "./locale-copy";
import { NAATI_COMPARE_LOCALE_ASIA } from "./compare-locale-asia";
export type {
  NaatiCompareLocaleRow,
  NaatiCompareLocaleItem,
  NaatiCompareLocaleCopy,
} from "./compare-locale-types";
import type { NaatiCompareLocaleCopy } from "./compare-locale-types";


const EN_TAIL =
  "Information current as of August 2026. Requirements change — always confirm with the authority that will receive your documents. Approval is at their discretion.";
const ZH_TAIL =
  "信息更新至 2026 年 8 月。各机构要求可能变动，递交前请与收件机构确认；是否受理由该机构裁量。";

export const NAATI_COMPARE_LOCALE: Partial<Record<NaatiLocale, NaatiCompareLocaleCopy>> = {
  en: {
    hreflang: "en-AU",
    hubTitle: "NAATI vs Consular, Notary, Apostille — Which One Do You Need",
    hubDescription:
      "Side-by-side comparisons of NAATI certified translation against MFA legalisation, notary public, apostille, sworn translators and self-translation, with a checklist for picking the right route first time.",
    hubH1: "Compare NAATI Certified Translation With Every Other Route",
    hubLead:
      "Short answer: NAATI certifies the translation; the Thai Department of Consular Affairs, a Notarial Services Attorney and an apostille certify the document or the signature. Australian and New Zealand agencies almost always ask for the first; most other destinations ask for the second. These pages compare each pair line by line so you order the right service once.",
    labels: {
      table: "Line-by-line comparison",
      chooseWhen: "Choose this when",
      faq: "Frequently asked questions",
      other: "Other comparisons",
      breadcrumbHome: "Home",
      breadcrumbNaati: "NAATI",
      breadcrumbCompare: "Compare",
      disclaimer:
        "Turnaround and fees are approximate ranges. Acceptance is decided by the receiving authority, not by the translator.",
      cta: "Not sure which route your case needs? Call 094-895-8999 or LINE @Thainotary and send a photo of the document — our team checks it before you order.",
    },
    items: [
      {
        slug: "naati-vs-consular-legalization",
        title: "NAATI Translation vs Thai MFA Consular Legalisation",
        description:
          "What NAATI certification proves versus what Thai Department of Consular Affairs legalisation proves, which destinations accept each, typical turnaround and whether you need both.",
        h1: "NAATI Certified Translation vs Thai MFA Legalisation",
        aLabel: "NAATI certified translation",
        bLabel: "Thai MFA (consular) legalisation",
        answer:
          "They certify different things. NAATI certifies that the translation is accurate, signed by a practitioner listed in the NAATI Practitioner Directory. The Thai Department of Consular Affairs certifies that the Thai document or the official's signature is genuine so it can be used abroad. For Australian or New Zealand authorities, a NAATI translation alone is normally enough. For other destinations, or for use inside Thailand, the consular route plus the destination embassy is usually required instead.",
        rows: [
          { aspect: "What is certified", a: "Accuracy of the translation", b: "Authenticity of the document or official signature" },
          { aspect: "Who certifies", a: "NAATI-certified practitioner (practitioner ID + expiry)", b: "Department of Consular Affairs, Thai MFA" },
          { aspect: "Common use", a: "Australian and New Zealand applications where practitioner credentials meet the receiving authority's rules", b: "Thai government, embassy or overseas filings that specify notarisation or legalisation" },
          { aspect: "Turnaround (approx.)", a: "1–3 business days", b: "2–5 business days, excluding embassy queues" },
          { aspect: "Verification", a: "Practitioner number searchable on naati.com.au", b: "Registry number and Garuda seal on the certificate" },
          { aspect: "Originals required", a: "High-quality scans usually accepted", b: "Original or agency-issued certified copy required" },
        ],
        chooseA: [
          "Lodging an Australian visa, PR or citizenship application",
          "Skills assessment (VETASSESS, ACS, ANMAC), AHPRA registration or university admission",
          "You need a lodgement-ready PDF within a few business days",
        ],
        chooseB: [
          "The destination country is not Australia or New Zealand",
          "The receiving authority explicitly asks for MFA plus embassy legalisation",
          "The document will be used for a legal act inside Thailand",
        ],
        faqs: [
          { q: "Do I need both NAATI and MFA legalisation?", a: "Usually not. Follow the written requirement of the authority receiving the document. Both are only needed when the same set is used in Thailand and in Australia. " + EN_TAIL },
          { q: "Can the MFA certify an English translation instead?", a: "The Department of Consular Affairs can certify Thai–English translations, but that certification does not replace a NAATI translation where the Australian authority specifically names NAATI." },
          { q: "Which is cheaper?", a: "It depends on page count and how many steps your destination requires. The MFA plus embassy chain normally involves several separate fees and a longer timeline. Ask for a quote based on your actual document set." },
        ],
      },
      {
        slug: "naati-vs-notary-public",
        title: "NAATI Translation vs Notary Public Certification in Thailand",
        description:
          "Why a Thai Notarial Services Attorney is not the same as a NAATI translator, what each signature actually certifies, and when a document needs both.",
        h1: "NAATI Certified Translation vs Notary Public (Thailand)",
        aLabel: "NAATI certified translation",
        bLabel: "Notarial Services Attorney (Thailand)",
        answer:
          "A NAATI practitioner certifies the wording of the translation. A Thai Notarial Services Attorney — a lawyer registered with the Lawyers Council of Thailand — certifies a signature, a copy or an oath, and says nothing about translation quality. Thailand has no civil-law notary public; the notarial attorney is the Thai equivalent. Many packages need both: the notarised original plus the NAATI translation of it.",
        rows: [
          { aspect: "What is certified", a: "The translation matches the source text", b: "The signature, the copy, or the declaration is genuine" },
          { aspect: "Legal basis", a: "NAATI certification scheme (Australia)", b: "Lawyers Council of Thailand notarial services attorney registration" },
          { aspect: "Covers translation quality", a: "Yes", b: "No" },
          { aspect: "Common use", a: "Visa, skills assessment, registration bodies", b: "Powers of attorney, affidavits, certified true copies, corporate documents" },
          { aspect: "Turnaround (approx.)", a: "1–3 business days", b: "Same day to 1 business day in most cases" },
        ],
        chooseA: ["The Australian or NZ authority asks for a certified translation", "Your document is in Thai and must be read in English"],
        chooseB: ["You are signing a power of attorney or affidavit for use abroad", "You need a certified true copy of a passport, degree or company record"],
        faqs: [
          { q: "Can a notary in Thailand certify my translation instead of NAATI?", a: "A notarial attorney can witness the translator's signature, but that is not a NAATI certification. Where NAATI is named in the requirement, only a NAATI practitioner satisfies it. " + EN_TAIL },
          { q: "Do I need notarisation before the NAATI translation?", a: "Only if the receiving authority asks for a notarised original. The translation can be produced from the notarised document so both stamps appear in the same bundle." },
          { q: "Is a Thai notarial attorney recognised overseas?", a: "Yes, when the signature is further legalised through the Thai MFA and, where required, the destination embassy. Recognition remains the receiving authority's decision." },
        ],
      },
      {
        slug: "naati-vs-apostille",
        title: "NAATI Translation vs Apostille — Thailand From 28 February 2027",
        description:
          "An apostille authenticates a public document; NAATI authenticates a translation. What changes for Thai documents once the Apostille Convention enters into force on 28 February 2027.",
        h1: "NAATI Certified Translation vs Apostille",
        aLabel: "NAATI certified translation",
        bLabel: "Apostille",
        answer:
          "An apostille never replaces a translation. It only replaces the embassy step in the legalisation chain between Hague Convention members. Thailand acceded to the HCCH Apostille Convention on 30 June 2026 and it enters into force for Thailand on 28 February 2027; until that date Thai documents still follow the MFA plus destination-embassy route. Even after that date, an Australian authority asking for a certified translation still needs a NAATI one.",
        rows: [
          { aspect: "What is certified", a: "Accuracy of the translation", b: "Authenticity of the seal and signature on a public document" },
          { aspect: "Issuer", a: "NAATI-certified practitioner", b: "Competent authority of the issuing state (Thai MFA for Thai documents)" },
          { aspect: "Status for Thailand", a: "Available today", b: "In force for Thailand from 28 February 2027 (acceded 30 June 2026)" },
          { aspect: "Replaces embassy step", a: "No", b: "Yes, between Convention members" },
          { aspect: "Covers translation", a: "Yes", b: "No — a separate translation is still needed" },
        ],
        chooseA: ["An Australian or NZ body asks for a certified translation", "You are lodging online and need a signed PDF"],
        chooseB: ["Your destination is a Convention member and asks for an apostille on the original", "You want to skip the destination-embassy step after February 2027"],
        faqs: [
          { q: "Does an apostille make my translation valid?", a: "No. An apostille authenticates the underlying public document or the official signature; the translation still needs its own certification. " + EN_TAIL },
          { q: "When exactly can Thai documents be apostilled?", a: "Thailand deposited its accession on 30 June 2026 and the Convention enters into force for Thailand on 28 February 2027. Before that date the MFA plus embassy chain remains the only route. Check hcch.net for the current status table." },
          { q: "Will Australia accept an apostilled Thai document without a translation?", a: "Not if the document is in Thai. Australian authorities require an English translation; where they name NAATI, it must be a NAATI one." },
        ],
      },
      {
        slug: "naati-vs-sworn-translator",
        title: "NAATI vs Sworn Translator — Different Countries, Different Systems",
        description:
          "Sworn, court-appointed and NAATI translators are separate national systems. Which one your destination recognises and why they are not interchangeable.",
        h1: "NAATI Certified Translator vs Sworn Translator",
        aLabel: "NAATI certified translator",
        bLabel: "Sworn / court-appointed translator",
        answer:
          "Both systems certify translation accuracy, but they are national and not interchangeable. NAATI is the Australian and New Zealand scheme. Sworn translators are appointed by a court or ministry in countries such as Germany, France, Spain, Italy and Poland. Thailand has no sworn-translator system; Thai translations for official use are certified by the translator and then legalised by the Department of Consular Affairs.",
        rows: [
          { aspect: "Recognised in", a: "Australia, New Zealand", b: "The specific country whose court or ministry made the appointment" },
          { aspect: "How appointed", a: "NAATI certification tests and recertification", b: "Court oath or ministry register" },
          { aspect: "Thai equivalent", a: "—", b: "None; MFA legalisation of the translation is used instead" },
          { aspect: "Verification", a: "NAATI Practitioner Directory", b: "National court or ministry register" },
        ],
        chooseA: ["Documents heading to Australia or New Zealand"],
        chooseB: ["Documents heading to Germany, France, Spain, Italy, Poland and similar sworn-translation jurisdictions"],
        faqs: [
          { q: "Can a NAATI translation be used in Germany?", a: "Usually not on its own. German authorities generally require a beglaubigte Übersetzung by a sworn translator registered with a German court. " + EN_TAIL },
          { q: "Is there a sworn translator in Thailand?", a: "No. Thai law has no sworn-translator register. Official Thai translations are certified by the translator and legalised by the Department of Consular Affairs." },
          { q: "Can you arrange sworn translations for European destinations?", a: "We can prepare the Thai-side certification and legalisation and coordinate with a sworn translator in the destination country where that is what the authority requires." },
        ],
      },
      {
        slug: "naati-vs-self-translation",
        title: "NAATI Translation vs Translating It Yourself",
        description:
          "Why self-translated or friend-translated documents are refused by Australian authorities, and the narrow cases where an uncertified translation is still accepted.",
        h1: "NAATI Certified Translation vs Self-Translation",
        aLabel: "NAATI certified translation",
        bLabel: "Self or informal translation",
        answer:
          "Where an authority names NAATI, a self-translation is not accepted — no matter how accurate it is, because the certification comes from an independent, verifiable practitioner. Some low-stakes cases (internal reference, an employer's HR file, a first-pass review) accept an uncertified translation. For visas, registrations and assessments, assume certification is required.",
        rows: [
          { aspect: "Independence", a: "Independent practitioner with a verifiable ID", b: "Applicant or relative — a conflict of interest" },
          { aspect: "Accepted for visas", a: "Yes, where NAATI is named", b: "Generally no" },
          { aspect: "Cost", a: "Per-page fee", b: "None" },
          { aspect: "Risk of resubmission", a: "Low when the format matches the source", b: "High — a refusal can cost weeks" },
        ],
        chooseA: ["Any lodgement to a government body, regulator or university"],
        chooseB: ["Internal reading copies only, with no official submission"],
        faqs: [
          { q: "I am a professional translator — can I translate my own documents?", a: "Even certified practitioners are normally barred from certifying their own documents because of the conflict of interest. " + EN_TAIL },
          { q: "Can a friend with good English translate for me?", a: "Not where the requirement names NAATI or a certified translator. The certification, not the language ability, is what is being checked." },
          { q: "What happens if I submit an uncertified translation?", a: "The common outcome is a request for further information and a delay; some authorities refuse the application outright. Requirements vary by agency." },
        ],
      },
      {
        slug: "naati-digital-vs-wet-stamp",
        title: "NAATI Digital PDF vs Wet-Stamped Hard Copy",
        description:
          "When a signed NAATI PDF is enough for an online lodgement and when you still need the physical stamped copy couriered to you.",
        h1: "NAATI Digital Certification vs Wet Stamp",
        aLabel: "Digitally certified PDF",
        bLabel: "Wet-stamped hard copy",
        answer:
          "Most online lodgements — ImmiAccount, skills assessments, university portals — accept a digitally certified NAATI PDF with the practitioner's stamp and signature. Counter submissions, some registry offices and certain court filings still ask for the physical wet-stamped copy. Order the hard copy when the requirement mentions originals or in-person lodgement.",
        rows: [
          { aspect: "Accepted online", a: "Yes, in most portals", b: "Yes, after scanning" },
          { aspect: "Delivery time (approx.)", a: "Same day to 2 business days by email", b: "Add courier time" },
          { aspect: "Counter or court lodgement", a: "Sometimes refused", b: "Preferred" },
          { aspect: "Reuse", a: "Unlimited copies of the same file", b: "One physical set per order" },
        ],
        chooseA: ["Online visa or assessment lodgement", "Tight deadline"],
        chooseB: ["In-person lodgement", "The requirement mentions original stamped documents"],
        faqs: [
          { q: "Is a scan of the wet stamp the same as a digital certification?", a: "Functionally similar for upload, but a digitally certified PDF carries the practitioner's digital signature, which some portals prefer. " + EN_TAIL },
          { q: "Can I order both?", a: "Yes. We commonly issue the PDF first for the upload deadline and courier the hard copy afterwards." },
          { q: "Does the digital version expire?", a: "The certification does not expire, but the practitioner's own NAATI certification has an expiry date shown on the stamp. Some authorities want the stamp valid at the date of lodgement." },
        ],
      },
      {
        slug: "naati-vs-inz-translation",
        title: "Australia DHA vs New Zealand INZ Translation Requirements",
        description:
          "How Immigration New Zealand's translation rules differ from the Department of Home Affairs, and when one certified set works for both.",
        h1: "DHA (Australia) vs INZ (New Zealand) Translation Rules",
        aLabel: "Department of Home Affairs (Australia)",
        bLabel: "Immigration New Zealand",
        answer:
          "DHA and INZ describe translator and certification requirements differently. Check the latest instructions for the application, where the translation is produced and the language direction; a credential accepted for one submission should not be assumed to satisfy the other.",
        rows: [
          { aspect: "Wording of the rule", a: "NAATI-certified translator for work done in Australia", b: "Reputable, independent translator with a certified statement" },
          { aspect: "NAATI accepted", a: "Yes", b: "Yes" },
          { aspect: "Translator declaration", a: "Stamp with practitioner ID", b: "Signed statement of accuracy plus credentials" },
          { aspect: "Offshore translators", a: "Accepted with full translator details", b: "Accepted" },
        ],
        chooseA: ["Any Australian visa, citizenship or assessment file"],
        chooseB: ["Immigration New Zealand applications, where a certified statement is the key requirement"],
        faqs: [
          { q: "Can one translation set be used for both countries?", a: "Often yes, when the set carries the NAATI stamp plus a statement of accuracy. Confirm the exact wording each agency asks for before you lodge. " + EN_TAIL },
          { q: "Does INZ require a NAATI translator specifically?", a: "INZ's published guidance asks for a reputable and independent translator; NAATI qualifies but is not the only route. Check the current INZ instructions." },
          { q: "What should I check for translations done in Thailand?", a: "Check each authority's latest instructions, the translator's credentials, language direction and required certification details before filing. The authority makes the final assessment." },
        ],
      },
      {
        slug: "naati-certified-vs-recognised",
        title: "NAATI Certified vs Recognised Practising — What the Stamp Means",
        description:
          "The difference between NAATI Certified Translator, Certified Provisional and Recognised Practising, and which one your authority will accept.",
        h1: "NAATI Certified vs Recognised Practising Translator",
        aLabel: "Certified Translator (CT)",
        bLabel: "Recognised Practising Translator (RPT)",
        answer:
          "Certified Translator is the tested credential and the one most authorities expect. Recognised Practising is granted for languages with no NAATI test available and is evidence of recent, regular practice rather than a passed exam. For Thai–English, certified practitioners exist, so submit a Certified Translator stamp unless the authority says otherwise.",
        rows: [
          { aspect: "How obtained", a: "Passing the NAATI certification test", b: "Evidence of recent practice where no test exists" },
          { aspect: "Available for Thai–English", a: "Yes", b: "Rarely relevant" },
          { aspect: "Typical acceptance", a: "Broadest", b: "Case by case" },
          { aspect: "Shown on the stamp", a: "Certification type, practitioner number, expiry", b: "Recognition type, practitioner number, expiry" },
        ],
        chooseA: ["Thai–English documents for any Australian authority"],
        chooseB: ["Rare language pairs with no NAATI test available"],
        faqs: [
          { q: "How do I check which credential my translator holds?", a: "Search the practitioner number from the stamp in the NAATI Practitioner Directory at naati.com.au; the listing shows the credential type and expiry. " + EN_TAIL },
          { q: "Is Certified Provisional accepted?", a: "It is a genuine NAATI credential, but some authorities prefer full Certified Translator. Ask before lodging if the requirement is strict." },
          { q: "Does an expired stamp invalidate an old translation?", a: "Translations certified while the credential was valid usually remain acceptable, but some authorities ask for a recent certification. Re-certification is quick if needed." },
        ],
      },
    ],
  },

  zh: {
    hreflang: "zh-Hans",
    hubTitle: "NAATI 与领事认证、公证、海牙认证的区别对照",
    hubDescription:
      "逐项对比 NAATI 认证翻译与泰国外交部领事认证、公证律师、海牙 Apostille、宣誓翻译及自行翻译，帮助您一次选对办理路径。",
    hubH1: "NAATI 认证翻译与其他认证方式全面对照",
    hubLead:
      "简答：NAATI 认证的是「译文准确性」，泰国外交部领事司、公证律师与 Apostille 认证的是「文件本身或签名的真实性」。递交澳大利亚、新西兰机构通常只需 NAATI；其他国家多走领事认证加使馆认证。以下逐组对照，帮助您避免重复缴费与退件。",
    labels: {
      table: "逐项对照表",
      chooseWhen: "以下情况请选择",
      faq: "常见问题",
      other: "其他对照主题",
      breadcrumbHome: "首页",
      breadcrumbNaati: "NAATI",
      breadcrumbCompare: "对照",
      disclaimer: "所列时间与费用均为约数区间；是否受理由收件机构裁量，非翻译方所能决定。",
      cta: "不确定您的情况该走哪条路径？请致电 094-895-8999 或加 LINE @Thainotary，把文件照片发给我们先行核对。",
    },
    items: [
      {
        slug: "naati-vs-consular-legalization",
        title: "NAATI 翻译与泰国外交部领事认证的区别",
        description: "NAATI 认证与泰国外交部领事司认证分别证明什么、各自被哪些机构接受、办理时间约多久、是否需要同时办理。",
        h1: "NAATI 认证翻译 vs 泰国外交部领事认证",
        aLabel: "NAATI 认证翻译",
        bLabel: "泰国外交部领事认证",
        answer:
          "两者证明的对象不同。NAATI 证明译文准确，由在 NAATI 译者名录中可查的持证译者签署；泰国外交部领事司证明文件或公务员签名属实，以便在境外使用。递交澳大利亚或新西兰机构，通常只需 NAATI 翻译；若目的地是其他国家，或文件要在泰国境内办理法律行为，则通常改走领事认证并加办目的国使馆认证。",
        rows: [
          { aspect: "证明内容", a: "译文与原件一致", b: "文件或公务签名属实" },
          { aspect: "签发方", a: "持 NAATI 证书的译者（含译者编号与有效期）", b: "泰国外交部领事司" },
          { aspect: "常见用途", a: "译员资质符合接收机构规定的澳大利亚及新西兰申请", b: "明确要求公证或领事认证的泰国政府、使馆或境外申请" },
          { aspect: "办理时间（约）", a: "1–3 个工作日", b: "2–5 个工作日，不含使馆排期" },
          { aspect: "可否核验", a: "可在 naati.com.au 查询译者编号", b: "凭认证编号与金翅鸟印鉴核验" },
          { aspect: "是否需要原件", a: "多数情况下高清扫描件即可", b: "需原件或机关出具的认证副本" },
        ],
        chooseA: ["申请澳洲签证、PR 或入籍", "职业评估、AHPRA 注册、澳新高校入学", "需要数个工作日内取得可直接上传的 PDF"],
        chooseB: ["目的地不是澳大利亚或新西兰", "收件机构明确要求外交部加使馆认证", "文件用于泰国境内的法律行为"],
        faqs: [
          { q: "需要同时办理 NAATI 与领事认证吗？", a: "多数情况下不需要，以收件机构的书面要求为准。仅当同一套文件既在泰国使用又在澳大利亚使用时才会两者并行。" + ZH_TAIL },
          { q: "领事司认证的英文译文能替代 NAATI 吗？", a: "领事司可认证泰英译文，但当澳方明确指名 NAATI 时，该认证不能替代 NAATI 翻译。" },
          { q: "哪一种更省钱？", a: "取决于页数与所需步骤数量。领事加使馆路径通常涉及多项收费且周期更长，建议按实际文件清单取报价。" },
        ],
      },
      {
        slug: "naati-vs-notary-public",
        title: "NAATI 翻译与泰国公证律师的区别",
        description: "泰国公证律师（Notarial Services Attorney）与 NAATI 译者的职能差异，各自签署的效力，以及何时需要同时办理。",
        h1: "NAATI 认证翻译 vs 泰国公证律师",
        aLabel: "NAATI 认证翻译",
        bLabel: "泰国公证律师",
        answer:
          "NAATI 译者证明译文用词准确；泰国公证律师是在泰国律师公会登记的律师，证明签名、副本或宣誓属实，不涉及译文质量。泰国没有大陆法系意义上的 Notary Public，公证律师即为对应角色。不少材料需要两者兼备：先公证原件，再对其做 NAATI 翻译。",
        rows: [
          { aspect: "证明内容", a: "译文与原文一致", b: "签名、副本或声明属实" },
          { aspect: "法律依据", a: "澳大利亚 NAATI 认证制度", b: "泰国律师公会公证律师登记制度" },
          { aspect: "是否涵盖译文质量", a: "是", b: "否" },
          { aspect: "常见用途", a: "签证、职业评估、注册机构", b: "授权书、宣誓书、认证副本、公司文件" },
          { aspect: "办理时间（约）", a: "1–3 个工作日", b: "多数当天至 1 个工作日" },
        ],
        chooseA: ["澳新机构要求认证翻译", "泰文文件需以英文呈交"],
        chooseB: ["签署境外使用的授权书或宣誓书", "需要护照、学位或公司登记的认证副本"],
        faqs: [
          { q: "泰国公证律师能替代 NAATI 认证译文吗？", a: "公证律师可以见证译者签名，但那不是 NAATI 认证。要求中指名 NAATI 时，只有 NAATI 持证译者符合。" + ZH_TAIL },
          { q: "要先公证再翻译吗？", a: "仅当收件机构要求公证原件时。可在公证后再翻译，使两个印鉴出现在同一套文件中。" },
          { q: "泰国公证律师的签署在境外被承认吗？", a: "经泰国外交部认证并（如需要）加办目的国使馆认证后通常可被承认，最终仍由收件机构裁量。" },
        ],
      },
      {
        slug: "naati-vs-apostille",
        title: "NAATI 翻译与海牙 Apostille（泰国 2027 年 2 月 28 日生效）",
        description: "Apostille 认证的是公文书本身，NAATI 认证的是译文。泰国自 2027 年 2 月 28 日起适用海牙认证后有何变化。",
        h1: "NAATI 认证翻译 vs 海牙 Apostille",
        aLabel: "NAATI 认证翻译",
        bLabel: "海牙 Apostille",
        answer:
          "Apostille 不能替代翻译，它只是在海牙公约成员国之间取代使馆认证这一步。泰国已于 2026 年 6 月 30 日加入海牙取消认证要求公约，并将于 2027 年 2 月 28 日对泰国生效；在此之前泰国文件仍走外交部加目的国使馆路径。即便生效之后，澳方要求认证翻译时仍需 NAATI。",
        rows: [
          { aspect: "证明内容", a: "译文准确性", b: "公文书上印章与签名的真实性" },
          { aspect: "签发方", a: "NAATI 持证译者", b: "出具国主管机关（泰国为外交部）" },
          { aspect: "泰国适用状态", a: "现已可办", b: "2027 年 2 月 28 日对泰国生效（2026 年 6 月 30 日加入）" },
          { aspect: "是否免除使馆认证", a: "否", b: "在公约成员国之间：是" },
          { aspect: "是否包含翻译", a: "是", b: "否，仍需另行翻译" },
        ],
        chooseA: ["澳新机构要求认证翻译", "线上递交需要带签名的 PDF"],
        chooseB: ["目的地为公约成员国且要求原件加签 Apostille", "希望在 2027 年 2 月后省去使馆步骤"],
        faqs: [
          { q: "办了 Apostille，译文就有效了吗？", a: "不会。Apostille 认证的是原始公文书或公务签名，译文仍需自身的认证。" + ZH_TAIL },
          { q: "泰国文件何时可以办 Apostille？", a: "泰国于 2026 年 6 月 30 日交存加入书，公约自 2027 年 2 月 28 日对泰国生效。在此之前仍只能走外交部加使馆路径，最新状态请查 hcch.net。" },
          { q: "澳方会接受未附译文的 Apostille 泰文文件吗？", a: "泰文文件仍须附英文译文；对方指名 NAATI 时须为 NAATI 译文。" },
        ],
      },
      {
        slug: "naati-vs-sworn-translator",
        title: "NAATI 与宣誓翻译（sworn translator）的区别",
        description: "宣誓翻译、法院指定翻译与 NAATI 属于不同国家制度，不可互换。您的目的地承认哪一种？",
        h1: "NAATI 认证译者 vs 宣誓翻译",
        aLabel: "NAATI 认证译者",
        bLabel: "宣誓／法院指定译者",
        answer:
          "两者都证明译文准确，但属于不同国家制度，不能互换。NAATI 是澳大利亚与新西兰的制度；宣誓译者由德国、法国、西班牙、意大利、波兰等国法院或部委任命。泰国没有宣誓译者制度，泰文官方译文由译者出具证明后再经外交部领事司认证。",
        rows: [
          { aspect: "承认地区", a: "澳大利亚、新西兰", b: "作出任命的该国" },
          { aspect: "取得方式", a: "通过 NAATI 考试并定期换证", b: "法院宣誓或部委注册" },
          { aspect: "泰国对应制度", a: "—", b: "无，改以外交部认证译文替代" },
          { aspect: "核验方式", a: "NAATI 译者名录", b: "该国法院或部委名册" },
        ],
        chooseA: ["文件递交澳大利亚或新西兰"],
        chooseB: ["文件递交德国、法国、西班牙、意大利、波兰等采用宣誓翻译制度的国家"],
        faqs: [
          { q: "NAATI 译文能在德国使用吗？", a: "通常不能单独使用。德国机关一般要求由德国法院登记的宣誓译者出具 beglaubigte Übersetzung。" + ZH_TAIL },
          { q: "泰国有宣誓译者吗？", a: "没有。泰国法律未设宣誓译者名册，官方译文由译者证明并经领事司认证。" },
          { q: "你们能安排欧洲国家的宣誓翻译吗？", a: "我们可完成泰国端的认证与领事认证，并在对方机关要求时协调目的国的宣誓译者。" },
        ],
      },
      {
        slug: "naati-vs-self-translation",
        title: "NAATI 认证翻译与自行翻译的区别",
        description: "自行或亲友翻译为何常被澳方退件，以及哪些低风险场景仍可使用未认证译文。",
        h1: "NAATI 认证翻译 vs 自行翻译",
        aLabel: "NAATI 认证翻译",
        bLabel: "自行或非正式翻译",
        answer:
          "当机构指名 NAATI 时，自行翻译不被接受——无论译得多准确，因为认证必须来自可核验的独立第三方。少数低风险场景（内部参考、雇主人事档案、初步审阅）可用未认证译文；涉及签证、注册与评估时请一律按需要认证处理。",
        rows: [
          { aspect: "独立性", a: "独立持证译者，编号可查", b: "本人或亲属，存在利害关系" },
          { aspect: "签证是否受理", a: "指名 NAATI 时：是", b: "通常否" },
          { aspect: "费用", a: "按页计费", b: "无" },
          { aspect: "补件风险", a: "格式对应时较低", b: "较高，退件可能延误数周" },
        ],
        chooseA: ["向政府机关、监管机构或高校递交的任何材料"],
        chooseB: ["仅供内部阅读、不作正式递交"],
        faqs: [
          { q: "我本身是专业译者，可以翻自己的文件吗？", a: "即便是持证译者，通常也不得认证自己的文件，因为存在利害冲突。" + ZH_TAIL },
          { q: "英文好的朋友帮忙翻译可以吗？", a: "在要求 NAATI 或认证译者的场合不行。机构核查的是认证资格，而非语言能力。" },
          { q: "提交未认证译文会怎样？", a: "常见结果是被要求补件并造成延误，部分机构会直接不予受理，具体依机构规定而定。" },
        ],
      },
      {
        slug: "naati-digital-vs-wet-stamp",
        title: "NAATI 电子 PDF 与实体钢印件的选择",
        description: "线上递交何时可用带签名的 NAATI PDF，何时仍需快递实体盖章件。",
        h1: "NAATI 电子认证 vs 实体盖章件",
        aLabel: "电子认证 PDF",
        bLabel: "实体盖章件",
        answer:
          "多数线上递交（ImmiAccount、职业评估、高校系统）接受带译者印鉴与签名的 NAATI 电子 PDF。柜台递交、部分登记机关与法院程序仍要求实体盖章件。当要求中提到「原件」或需现场递交时，请加订实体件。",
        rows: [
          { aspect: "线上受理", a: "多数系统：是", b: "扫描后同样可用" },
          { aspect: "交付时间（约）", a: "当天至 2 个工作日电邮交付", b: "另加快递时间" },
          { aspect: "柜台或法院递交", a: "有时不被接受", b: "较受偏好" },
          { aspect: "重复使用", a: "同一文件可无限次使用", b: "每次下单一套实体件" },
        ],
        chooseA: ["线上签证或评估递交", "时间紧迫"],
        chooseB: ["需现场递交", "要求中提及原件盖章文件"],
        faqs: [
          { q: "实体件的扫描件等同于电子认证吗？", a: "上传用途上相近，但电子认证 PDF 带有译者数字签名，部分系统更偏好。" + ZH_TAIL },
          { q: "可以两种都要吗？", a: "可以。常见做法是先出 PDF 赶上传期限，随后快递实体件。" },
          { q: "电子版会过期吗？", a: "认证本身不过期，但印鉴上译者的 NAATI 证书有有效期，部分机构要求递交当日该证书仍在有效期内。" },
        ],
      },
      {
        slug: "naati-vs-inz-translation",
        title: "澳大利亚 DHA 与新西兰 INZ 的翻译要求差异",
        description: "新西兰移民局与澳大利亚内政部对译文的措辞要求不同，一套 NAATI 译文能否同时使用。",
        h1: "DHA（澳大利亚） vs INZ（新西兰）翻译要求",
        aLabel: "澳大利亚内政部 DHA",
        bLabel: "新西兰移民局 INZ",
        answer:
          "DHA 与 INZ 对译员资质和认证信息的表述不同。应根据申请类型、翻译地点及语言方向核对最新说明，不应假定一套资质自动满足两个机构。",
        rows: [
          { aspect: "规定措辞", a: "澳境内翻译须为 NAATI 持证译者", b: "信誉良好的独立译者并附认证声明" },
          { aspect: "是否接受 NAATI", a: "是", b: "是" },
          { aspect: "译者声明", a: "含译者编号的印鉴", b: "签署的准确性声明与资历" },
          { aspect: "境外译者", a: "载明完整译者信息即可", b: "接受" },
        ],
        chooseA: ["任何澳大利亚签证、入籍或评估材料"],
        chooseB: ["新西兰移民申请，以认证声明为关键要求"],
        faqs: [
          { q: "一套译文能否两国通用？", a: "在同时具备 NAATI 印鉴与准确性声明时通常可以；递交前请核对各机构的具体措辞。" + ZH_TAIL },
          { q: "INZ 是否必须 NAATI？", a: "INZ 公布的指引要求信誉良好且独立的译者，NAATI 符合但并非唯一途径，请以 INZ 现行指引为准。" },
          { q: "在泰国完成翻译应检查什么？", a: "提交前应分别核对接收机构的最新说明、译员资质、语言方向和认证信息；最终受理由接收机构判断。" },
        ],
      },
      {
        slug: "naati-certified-vs-recognised",
        title: "NAATI Certified 与 Recognised Practising 的区别",
        description: "Certified Translator、Certified Provisional 与 Recognised Practising 有何不同，您的机构会接受哪一种。",
        h1: "NAATI Certified vs Recognised Practising 译者",
        aLabel: "Certified Translator（CT）",
        bLabel: "Recognised Practising Translator（RPT）",
        answer:
          "Certified Translator 为通过考试取得的资格，也是多数机构所期待的等级；Recognised Practising 适用于 NAATI 尚未开设考试的语种，证明的是近期持续执业而非通过考试。泰英语对存在持证译者，因此除非机构另有说明，请提交 Certified Translator 的印鉴。",
        rows: [
          { aspect: "取得方式", a: "通过 NAATI 认证考试", b: "无考试语种，凭近期执业证明" },
          { aspect: "泰英语对是否适用", a: "是", b: "极少相关" },
          { aspect: "受理范围", a: "最广", b: "个案判断" },
          { aspect: "印鉴显示", a: "认证类别、译者编号、有效期", b: "认可类别、译者编号、有效期" },
        ],
        chooseA: ["递交澳方机构的泰英文件"],
        chooseB: ["NAATI 尚无考试的稀有语种"],
        faqs: [
          { q: "如何确认译者持有哪种资格？", a: "以印鉴上的译者编号在 naati.com.au 译者名录查询，列表会显示资格类别与有效期。" + ZH_TAIL },
          { q: "Certified Provisional 会被接受吗？", a: "它是正式的 NAATI 资格，但部分机构偏好完整的 Certified Translator，要求严格时请先行确认。" },
          { q: "印鉴过期会使旧译文失效吗？", a: "在资格有效期内完成的认证通常仍可使用，但部分机构要求近期认证；如有需要可快速重新出具。" },
        ],
      },
    ],
  },
  ...NAATI_COMPARE_LOCALE_ASIA,
};


export const NAATI_COMPARE_LOCALES = Object.keys(NAATI_COMPARE_LOCALE) as NaatiLocale[];

export function naatiCompareLocaleItem(locale: NaatiLocale, slug: string) {
  return NAATI_COMPARE_LOCALE[locale]?.items.find((i) => i.slug === slug);
}
